import { useEffect, useState } from "react";
import { Copy, LogOut, Users, Settings, Package, RefreshCw, Shield, BarChart3 } from "lucide-react";
import TeamMembers from "./TeamMembers";
import { toast } from "sonner";
import { listClienti, OBIETTIVO_LABEL, CLUB_BASE } from "./clubApi";
import { Btn, Card, Pill, Logo } from "./ui";
import ClientDrawer from "./ClientDrawer";
import TeamSettings from "./TeamSettings";
import Catalogo from "./Catalogo";
import StatiGuide from "./StatiGuide";
import StatsTab from "./StatsTab";

const TONE = { nuovo: "gold", riordino: "red", checkin: "blue", percorso: "green" };
const fmt = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });

export default function TeamDashboard({ user, onUser, onLogout }) {
  const [tab, setTab] = useState("clienti");
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("tutti");
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); listClienti().then(setRows).catch(() => toast.error("Non riesco a caricare i clienti")).finally(() => setLoading(false)); };
  useEffect(load, []);

  const link = `${window.location.origin}${CLUB_BASE}/i/${user.slug}`;
  const copy = async () => { try { await navigator.clipboard.writeText(link); toast.success("Link copiato"); } catch { toast.error("Copia non riuscita: seleziona il link e copialo a mano"); } };
  const counts = rows.reduce((a, r) => ({ ...a, [r.stato.key]: (a[r.stato.key] || 0) + 1 }), {});
  const shown = filter === "tutti" ? rows : rows.filter((r) => r.stato.key === filter);

  return (
    <div className="mx-auto max-w-3xl px-5 pt-6 pb-16" data-testid="team-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-3">
        <div className="flex items-center gap-3 min-w-0"><Logo size={34} /><div className="min-w-0"><p className="font-club-display text-lg leading-none whitespace-nowrap">Diamond Club</p><p className="text-[0.6rem] uppercase tracking-[0.22em] text-[#9A948A] mt-1 truncate">{user.role === "admin" ? "Amministratore" : "Incaricata"} · {user.name}</p></div></div>
        <div className="flex items-center gap-3 shrink-0">
          {user.role === "admin" && <Pill className="whitespace-nowrap" data-testid="admin-badge">Sei amministratore</Pill>}
          <button onClick={onLogout} className="text-xs text-[#9A948A] hover:text-white flex items-center gap-1" data-testid="team-logout-btn"><LogOut size={14} /> Esci</button>
        </div>
      </div>

      <Card className="mt-6 border-[#E5B13A]/30" data-testid="my-link-card">
        <p className="text-[0.65rem] uppercase tracking-[0.2em] text-[#9A948A]">Il tuo link per il test</p>
        <div className="mt-2 flex items-center gap-2">
          <code className="flex-1 truncate rounded-xl bg-[#0E0E10] px-3 py-2 text-sm text-[#F5D27A]" data-testid="my-link">{link}</code>
          <Btn variant="dark" className="px-4 py-2" onClick={copy} data-testid="copy-link-btn"><Copy size={14} /> Copia</Btn>
        </div>
        <p className="mt-2 text-xs text-[#6F6A62]">Mandalo su WhatsApp: «Fai il test di 3 minuti e ricevi il tuo percorso personale, gratis».</p>
      </Card>

      <div className="mt-6 flex gap-1 border-b border-[#26262B] overflow-x-auto">
        {[["clienti", "Clienti", Users], ["impostazioni", "Il mio profilo", Settings], ...(user.role === "admin" ? [["catalogo", "Catalogo", Package], ["team", "Team", Shield], ["stats", "Statistiche", BarChart3]] : [])].map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`team-tab-${id}`} className={`flex shrink-0 items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === id ? "border-[#E5B13A] text-white" : "border-transparent text-[#9A948A] hover:text-white"}`}><Icon size={15} />{label}</button>
        ))}
      </div>

      {tab === "clienti" && (
        <div className="mt-6">
          <div className="flex flex-wrap items-center gap-2">
            {[["tutti", `Tutti (${rows.length})`], ["nuovo", `Piano da preparare (${counts.nuovo || 0})`], ["checkin", `Check (${counts.checkin || 0})`], ["riordino", `Riordino (${counts.riordino || 0})`], ["percorso", `In percorso (${counts.percorso || 0})`]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`} className={`rounded-full border px-3 py-1.5 text-xs font-medium ${filter === k ? "border-[#E5B13A] text-[#F5D27A] bg-[#E5B13A]/10" : "border-[#2E2E34] text-[#9A948A]"}`}>{l}</button>
            ))}
            <button onClick={load} className="ml-auto text-[#9A948A] hover:text-white" title="Aggiorna" data-testid="reload-btn"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /></button>
          </div>
          <StatiGuide />
          {!loading && shown.length === 0 && (
            <Card className="mt-6 text-center text-sm text-[#9A948A]" data-testid="empty-clients">Nessun cliente qui. Manda il tuo link: il primo test arriva da lì.</Card>
          )}
          <div className="mt-4 space-y-2">
            {shown.map((r) => (
              <button key={r.id} onClick={() => setOpen(r.id)} data-testid={`client-row-${r.id}`} className="w-full text-left rounded-2xl border border-[#26262B] bg-[#141417] p-4 hover:border-[#E5B13A]/50 transition-colors">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{r.nome} <span className="font-club-display text-[#E5B13A] ml-2">{r.indice}</span></p>
                    <p className="text-xs text-[#9A948A] mt-0.5 truncate">{fmt(r.created_at)} · {r.obiettivi.map((o) => OBIETTIVO_LABEL[o] || o).join(", ")}{user.role === "admin" ? ` · ${r.incaricata}` : ""}</p>
                  </div>
                  <Pill tone={TONE[r.stato.key]}>{r.stato.label}</Pill>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {tab === "impostazioni" && <TeamSettings user={user} onUser={onUser} />}
      {tab === "catalogo" && <Catalogo />}
      {tab === "team" && <TeamMembers me={user} />}
      {tab === "stats" && <StatsTab />}
      {open && <ClientDrawer id={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
